import { useEffect, useState } from "react";
import "@/App.css";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Seo from "@/components/Seo";
import { Toaster, toast } from "sonner";
import {
  Globe,
  Code2,
  Zap,
  Layout,
  ShoppingCart,
  Mail,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  Instagram,
} from "lucide-react";

const FORMSPREE_ENDPOINT =
  process.env.REACT_APP_FORMSPREE_ENDPOINT || "https://formspree.io/f/mpqjjqzd";

const FacebookBrandIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 320 512" className={className} aria-hidden="true" fill="currentColor">
    <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
  </svg>
);

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "navbar-glass py-2" : "bg-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("hero");
          }}
          className="flex items-center"
          data-testid="logo"
        >
          <img
            src="/Logo.png"
            alt="Web & Studio Logo"
            className="h-[80px] md:h-[88px] lg:h-[96px] w-auto object-contain"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <button
            onClick={() => scrollToSection("servicos")}
            className="nav-link text-slate-600 hover:text-slate-900 font-medium transition-colors"
            data-testid="nav-services"
          >
            Serviços
          </button>
          <button
            onClick={() => scrollToSection("orcamentos")}
            className="nav-link text-slate-600 hover:text-slate-900 font-medium transition-colors"
            data-testid="nav-budgets"
          >
            Orçamentos
          </button>
          <button
            onClick={() => scrollToSection("trabalhos")}
            className="nav-link text-slate-600 hover:text-slate-900 font-medium transition-colors"
            data-testid="nav-work"
          >
            Trabalhos
          </button>
          <button
            onClick={() => scrollToSection("sobre")}
            className="nav-link text-slate-600 hover:text-slate-900 font-medium transition-colors"
            data-testid="nav-about"
          >
            Sobre Nós
          </button>
          <button
            onClick={() => scrollToSection("contacto")}
            className="nav-link text-slate-600 hover:text-slate-900 font-medium transition-colors"
            data-testid="nav-contact"
          >
            Contacto
          </button>
          <Button
            onClick={() => scrollToSection("contacto")}
            className="btn-primary bg-teal-700 hover:bg-teal-800 text-white rounded-full px-6"
            data-testid="nav-cta"
          >
            Começar Projeto
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-slate-700"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Abrir menu"
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 py-4 animate-fade-in"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col px-6 gap-4">
            <button
              onClick={() => scrollToSection("servicos")}
              className="text-left text-slate-600 hover:text-slate-900 font-medium py-2"
            >
              Serviços
            </button>
            <button
              onClick={() => scrollToSection("orcamentos")}
              className="text-left text-slate-600 hover:text-slate-900 font-medium py-2"
            >
              Orçamentos
            </button>
            <button
              onClick={() => scrollToSection("trabalhos")}
              className="text-left text-slate-600 hover:text-slate-900 font-medium py-2"
            >
              Trabalhos
            </button>
            <button
              onClick={() => scrollToSection("sobre")}
              className="text-left text-slate-600 hover:text-slate-900 font-medium py-2"
            >
              Sobre Nós
            </button>
            <button
              onClick={() => scrollToSection("contacto")}
              className="text-left text-slate-600 hover:text-slate-900 font-medium py-2"
            >
              Contacto
            </button>
            <Button
              onClick={() => scrollToSection("contacto")}
              className="btn-primary bg-teal-700 hover:bg-teal-800 text-white rounded-full w-full"
            >
              Começar Projeto
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contacto");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="min-h-screen flex items-center bg-[#FAFAFA] pt-14"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-2 animate-fade-in-up">
              <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
                Estúdio de Desenvolvimento Web
              </span>
              <div className="section-line"></div>
            </div>

            <h1
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-slate-900 animate-fade-in-up delay-100"
              style={{ opacity: 0, animationFillMode: "forwards" }}
            >
              Websites que <span className="text-teal-700">funcionam</span> para o
              seu negócio.
            </h1>

            <p
              className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-xl animate-fade-in-up delay-200"
              style={{ opacity: 0, animationFillMode: "forwards" }}
            >
              Design moderno, desenvolvimento robusto e foco em resultados. A Web
              &amp; Studio cria a sua presença digital.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300"
              style={{ opacity: 0, animationFillMode: "forwards" }}
            >
              <Button
                onClick={scrollToContact}
                className="btn-primary bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-teal-700/20"
                data-testid="hero-cta"
              >
                Começar Projeto
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  document
                    .getElementById("servicos")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="rounded-full px-8 py-6 text-base font-medium border-slate-300 hover:bg-slate-100"
                data-testid="hero-secondary-cta"
              >
                Ver Serviços
              </Button>
            </div>
          </div>

          {/* Right Visual */}
          <div
            className="relative animate-fade-in-up delay-400 hidden lg:block"
            style={{ opacity: 0, animationFillMode: "forwards" }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-teal-100 to-teal-50 rounded-2xl -z-10"></div>
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80"
                alt="Desenvolvimento web moderno"
                className="w-full rounded-lg shadow-2xl"
              />
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Zap className="h-6 w-6 text-teal-700" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Entrega Rápida
                    </p>
                    <p className="text-xs text-slate-500">Sites prontos em dias</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Globe,
      title: "Sites Institucionais",
      description:
        "Websites profissionais que transmitem confiança e credibilidade. Perfeitos para empresas que querem marcar presença online.",
      featured: true,
    },
    {
      icon: Code2,
      title: "Desenvolvimento à Medida",
      description:
        "Soluções personalizadas adaptadas às necessidades específicas do seu negócio. Funcionalidades únicas sem limitações.",
      featured: false,
    },
    {
      icon: Zap,
      title: "Otimização de Performance",
      description:
        "Sites rápidos que carregam em segundos. Melhoramos a velocidade e experiência para converter mais visitantes.",
      featured: false,
    },
    {
      icon: Layout,
      title: "Adaptação UX",
      description:
        "Redesenhamos e adaptamos conteúdos para uma melhor experiência do utilizador em todos os dispositivos.",
      featured: true,
    },
  ];

  return (
    <section
      id="servicos"
      className="py-24 md:py-32 bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 md:mb-20">
          <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
            Serviços
          </span>
          <div className="section-line mt-2 mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
            O que fazemos
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            Soluções web focadas na simplicidade e eficácia. Cada projeto é pensado
            para comunicar bem e facilitar o contacto com potenciais clientes.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`service-card bg-white border border-slate-100 p-8 md:p-10 rounded-lg ${
                service.featured ? "md:row-span-1" : ""
              }`}
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center mb-6">
                <service.icon
                  className="h-7 w-7 text-teal-700"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="text-xl md:text-2xl font-medium text-slate-900 mb-3">
                {service.title}
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-600">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Budgets Section
const BudgetsSection = () => {
  const budgets = [
    {
      id: "essencial",
      name: "Essencial",
      price: "Desde 150€",
      summary: "Ideal para presença online simples e profissional.",
      highlighted: false,
      items: [
        "Website one-page responsivo",
        "Design personalizado da marca",
        "Formulário de contacto",
        "SEO base e integração de Google Maps",
      ],
    },
    {
      id: "negocio",
      name: "Negócio",
      price: "Desde 350€",
      summary: "Para empresas que precisam de estrutura e conversão.",
      highlighted: true,
      items: [
        "Até 6 páginas (Home, Serviços, Sobre, Contacto, etc.)",
        "Copywriting base orientado a conversão",
        "Integração com WhatsApp e redes sociais",
        "Otimização de velocidade + SEO on-page",
      ],
    },
    {
      id: "loja-online",
      name: "Loja Online",
      price: "Desde 650€",
      summary: "Solução completa para catálogo e gestão de produtos online.",
      highlighted: false,
      items: [
        "Catálogo de produtos e categorias",
        "Pedido de contacto por formulário e WhatsApp",
        "Configuração de logística e regras de stock",
        "Formação rápida para gestão do catálogo",
      ],
    },
  ];

  return (
    <section id="orcamentos" className="py-24 md:py-32 bg-slate-50" data-testid="budgets-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 md:mb-20">
          <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
            Orçamentos
          </span>
          <div className="section-line mt-2 mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Escolhe o pacote certo para o teu negócio
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">
            Três opções claras para começares. Se precisares de algo específico,
            fazemos um orçamento 100% personalizado.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {budgets.map((budget, index) => {
            return (
              <article
                key={budget.name}
                className={`rounded-2xl border p-8 md:p-10 bg-white transition-all ${
                  budget.highlighted
                    ? "border-teal-300 shadow-xl shadow-teal-900/5"
                    : "border-slate-200 shadow-sm"
                }`}
                data-testid={`budget-card-${index}`}
              >
                {budget.highlighted && (
                  <p className="inline-flex rounded-full bg-teal-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-teal-800">
                    Mais escolhido
                  </p>
                )}

                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-900">{budget.name}</h3>
                    <p className="mt-2 text-slate-600">{budget.summary}</p>
                  </div>
                  <div className="h-11 w-11 rounded-xl bg-teal-50 flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-teal-700" />
                  </div>
                </div>

                <p className="mt-6 text-3xl font-bold tracking-tight text-slate-900">
                  {budget.price}
                </p>

                <ul className="mt-6 space-y-3">
                  {budget.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-slate-700">
                      <CheckCircle2 className="h-5 w-5 text-teal-700 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/planos/${encodeURIComponent(budget.id)}`}
                  className="mt-8 inline-flex items-center justify-center rounded-full bg-teal-700 px-6 py-3 font-medium text-white transition-colors hover:bg-teal-800"
                >
                  Ver detalhes
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Work Examples Section
const WorkExamplesSection = () => {
  const projects = [
    {
      title: "Belle Beauté",
      category: "Salão de Beleza",
      description:
        "Template premium para salão com hero elegante, tabela de preços, galeria e agendamento.",
      url: "/demo/salon",
      image:
        "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?w=900&q=80",
      deliverables: ["Tabela de preços", "Botão de marcação"],
    },
    {
      title: "Sabores da Terra",
      category: "Restaurante",
      description:
        "Template para restauração com menu por categorias, reserva de mesa e secções de galeria.",
      url: "/demo/restaurant",
      image:
        "https://images.unsplash.com/photo-1768162125972-724be0bbc063?w=900&q=80",
      deliverables: ["Menu estruturado", "CTA de reserva"],
    },
    {
      title: "Studio Noir",
      category: "Fotografia",
      description:
        "Portfólio visual para fotógrafos com destaque para galeria, serviços e contacto minimalista.",
      url: "/demo/photographer",
      image:
        "https://images.unsplash.com/photo-1768248468040-259dc015e0d9?w=900&q=80",
      deliverables: ["Galeria imersiva", "Secção de serviços"],
    },
    {
      title: "AutoPro Garage",
      category: "Oficina Mecânica",
      description:
        "Website técnico para oficina com serviços, diferenciais da marca e pedido de orçamento.",
      url: "/demo/mechanic",
      image:
        "https://images.unsplash.com/photo-1691840204491-f5c608613b68?w=900&q=80",
      deliverables: ["Lista de serviços", "Pedido de orçamento"],
    },
  ];

  return (
    <section
      id="trabalhos"
      className="py-24 md:py-32 bg-[#FAFAFA]"
      data-testid="work-examples-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 md:mb-20">
          <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
            Trabalhos
          </span>
          <div className="section-line mt-2 mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Exemplos de projetos
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            Tipos de websites que criamos para empresas e profissionais que querem
            comunicar melhor e converter mais.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <article
              key={project.title}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              data-testid={`work-example-card-${index}`}
            >
              <Link
                to={project.url}
                className="block relative h-52 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700/40"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                  {project.category}
                </span>
              </Link>

              <div className="space-y-4 p-6">
                <h3 className="text-xl font-medium text-slate-900">{project.title}</h3>
                <p className="text-slate-600 leading-relaxed">{project.description}</p>

                <div className="space-y-2">
                  {project.deliverables.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-teal-700 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="grid gap-3 pt-1 sm:grid-cols-2">
                  <Link
                    to={project.url}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                    data-testid={`work-example-preview-${index}`}
                  >
                    Ver demo
                  </Link>
                  <Link
                    to={`/briefing?estilo=${encodeURIComponent(project.title)}&categoria=${encodeURIComponent(
                      project.category
                    )}&demo=${encodeURIComponent(project.url)}`}
                    className="inline-flex items-center justify-center rounded-full bg-teal-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-800"
                    data-testid={`work-example-booking-${index}`}
                  >
                    Quero este estilo
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section
      id="sobre"
      className="py-24 md:py-32 bg-[#FAFAFA]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl -z-10"></div>
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
              alt="Equipa Web & Studio"
              className="w-full rounded-lg shadow-xl"
            />
          </div>

          {/* Content */}
          <div className="space-y-8 order-1 lg:order-2">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
                Sobre Nós
              </span>
              <div className="section-line mt-2 mb-6"></div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
                Simplicidade e eficácia
              </h2>
            </div>

            <p className="text-lg leading-relaxed text-slate-600">
              Somos um estúdio focado na criação de websites simples, modernos e
              profissionais. Acreditamos que um bom site não precisa de ser
              complicado, precisa de funcionar bem e comunicar claramente.
            </p>

            <p className="text-lg leading-relaxed text-slate-600">
              Ajudamos empresas, pequenos negócios e profissionais independentes a
              criar uma presença online clara, eficaz e adaptada às necessidades do
              mercado digital atual.
            </p>

            <div className="space-y-4 pt-4">
              {[
                "Rapidez de entrega",
                "Atenção ao detalhe",
                "Abordagem direta e focada em resultados",
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-teal-700 flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", message: "", consent: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitWithBrowserPost = ({ name, email, message }) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = FORMSPREE_ENDPOINT;
    form.style.display = "none";

    const nextUrl = `${window.location.origin}/thanks?name=${encodeURIComponent(name || "")}`;
    const fields = {
      name,
      email,
      message,
      _subject: `Novo contacto de ${name || "Website"}`,
      _replyto: email,
      _next: nextUrl,
    };

    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value || "";
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    const consent = formData.consent;

    if (!consent) {
      toast.error("Precisas de aceitar a Política de Privacidade para enviar.");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = new FormData();
      payload.append("name", name);
      payload.append("email", email);
      payload.append("message", message);
      payload.append("privacy_consent", "yes");
      payload.append("_subject", `Novo contacto de ${name || "Website"}`);
      payload.append("_replyto", email);

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: payload,
      });

      if (!res.ok) {
        const errorPayload = await res.json().catch(() => null);
        const errorMessage = errorPayload?.error || "";
        const ajaxBlockedByFormspree =
          res.status === 403 && /submit via AJAX|reCAPTCHA|custom key/i.test(errorMessage);

        if (ajaxBlockedByFormspree) {
          submitWithBrowserPost({ name, email, message });
          return;
        }

        throw new Error(errorMessage || "Formspree error");
      }

      toast.success("Mensagem enviada!", {
        description: "Vamos responder o mais rápido possível.",
      });

      setFormData({ name: "", email: "", message: "", consent: false });
      setIsSubmitting(false);

      // Vai para a tua página /thanks
      navigate("/thanks", { state: { name } });
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar mensagem", {
        description: "Não foi possível enviar agora. Vamos tentar novamente em breve.",
      });
      setIsSubmitting(false);
      navigate("/thanks", { state: { name, submissionFailed: true } });
    }
  };

  return (
    <section
      id="contacto"
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <span className="text-sm font-medium tracking-wide uppercase text-teal-700">
                Contacto
              </span>
              <div className="section-line mt-2 mb-6"></div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
                Vamos conversar?
              </h2>
            </div>

            <p className="text-lg leading-relaxed text-slate-600 max-w-md">
              Tem um projeto em mente? Gostaria de saber mais sobre os nossos
              serviços? Entre em contacto connosco.
            </p>

            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                  <Mail className="h-5 w-5 text-teal-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a
                    href="mailto:contacto@webestudio.pt"
                    className="text-lg font-medium text-slate-900 hover:text-teal-700 transition-colors"
                    data-testid="contact-email"
                  >
                    contacto@webestudio.pt
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-2xl p-8 md:p-10">
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="contact-form"
            >
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Nome
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="O seu nome"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-custom bg-white border-slate-200 h-12 focus:border-teal-500 focus:ring-teal-500/20"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="o.seu@email.pt"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-custom bg-white border-slate-200 h-12 focus:border-teal-500 focus:ring-teal-500/20"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Mensagem
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Conte-nos sobre o seu projeto..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="input-custom bg-white border-slate-200 resize-none focus:border-teal-500 focus:ring-teal-500/20"
                  data-testid="contact-message-input"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-700/30"
                  data-testid="contact-consent-checkbox"
                />
                <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed">
                  Li e aceito a{" "}
                  <Link to="/privacy" className="text-teal-700 hover:text-teal-800 font-medium">
                    Política de Privacidade
                  </Link>
                  .
                </label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-6 text-base font-medium shadow-lg shadow-teal-700/20"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "A enviar..." : "Enviar Mensagem"}
                {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="bg-slate-50 py-12 border-t border-slate-100"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative flex flex-col items-center gap-3">
          <p className="text-slate-900 font-semibold text-center">Web & Studio</p>
          <p className="text-sm text-slate-500 text-center">© {currentYear} Web & Studio</p>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-700 transition-colors">
              Privacidade
            </Link>
            <Link to="/cookies" className="hover:text-slate-700 transition-colors">
              Cookies
            </Link>
            <Link to="/terms" className="hover:text-slate-700 transition-colors">
              Termos
            </Link>
          </div>
          <div className="absolute right-0 top-0 flex items-center gap-3">
            <a
              href="https://www.instagram.com/webstudio026/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-black hover:text-black/70 transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61587668724425"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-black hover:text-black/70 transition-colors"
            >
              <FacebookBrandIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function Home() {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const sectionId = hash.replace("#", "");
    const section = document.getElementById(sectionId);
    if (!section) return;

    section.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Seo
        title="Web & Studio | Websites Profissionais para Negócios"
        description="Criamos websites simples, modernos e profissionais para empresas e pequenos negócios. Design, desenvolvimento e foco em resultados."
        path="/"
      />
      <Toaster richColors position="top-right" />
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <BudgetsSection />
        <WorkExamplesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}





